import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Toaster, toast } from 'sonner';
import { ChevronLeft, ChevronRight, Send, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';

import { translations, formatCurrency, formatDate } from '@/lib/translations';
import ProgressSteps from './ProgressSteps';
import RoleSelectionSection from './RoleSelectionSection';
import CustomerInfoSection from './CustomerInfoSection';
import ProductDetailsSection from './ProductDetailsSection';
import PaymentSection from './PaymentSection';
import TermsSection from './TermsSection';
import SignatureSection from './SignatureSection';
import SendToCustomerSection from './SendToCustomerSection';
import PaymentReservationSection from './PaymentReservationSection';
import SuccessScreen from './SuccessScreen';

const initialFormData = {
  userRole: 'customer', // 'customer' or 'salesAgent'
  customerInfo: {
    salutation: 'mr',
    customerNumber: '',
    name: '',
    companyName: '',
    commercialRegister: '',
    vatId: '',
    address: '',
    phone: '',
    email: ''
  },
  productDetails: {
    quantity: 1,
    unitPrice: 58500,
    customUnitPrice: null,
    grossPrice: 58500,
    discount: 0,
    discountPercent: 0,
    discountAmount: 0,
    netPrice: 58500,
    subtotal: 58500,
    vat: 11115,
    vatAmount: 11115,
    totalPrice: 69615
  },
  usePaymentPlan: true,
  termsAccepted: false,
  signature: {
    name: '',
    companyName: '',
    location: '',
    date: new Date(),
    signatureData: '',
    typedSignature: '',
    signatureType: 'draw',
    confirmationEmail: ''
  }
};

export const ContractForm = ({ language }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSentToCustomer, setIsSentToCustomer] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isGeneratingCustomerNumber, setIsGeneratingCustomerNumber] = useState(false);
  const [contractId, setContractId] = useState(null);
  const [submittedAt, setSubmittedAt] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const t = translations[language];
  const locale = language === 'de' ? 'de-DE' : 'en-US';

  // Dynamic steps based on role
  const activeSteps = useMemo(() => {
    const isCustomer = formData.userRole === 'customer';
    
    let steps = ['role', 'customer', 'product', 'payment', 'terms'];
    
    // For customer: add signature, then reservation payment
    // For sales agent: add sendToCustomer
    if (isCustomer) {
      steps.push('signature');
      steps.push('reservation'); // Add reservation payment step after signature
    } else {
      steps.push('sendToCustomer');
    }
    
    return steps;
  }, [formData.userRole]);

  // Get the actual step key for current index
  const currentStepKey = activeSteps[currentStep];

  // Validation functions
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateStep = (stepKey) => {
    const newErrors = {};

    switch (stepKey) {
      case 'role':
        // No validation needed - always has a default value
        break;

      case 'customer':
        if (!formData.customerInfo.name.trim()) {
          newErrors.name = t.validation.required;
        }
        if (!formData.customerInfo.address.trim()) {
          newErrors.address = t.validation.required;
        }
        if (!formData.customerInfo.phone.trim()) {
          newErrors.phone = t.validation.required;
        }
        if (!formData.customerInfo.email.trim()) {
          newErrors.email = t.validation.required;
        } else if (!validateEmail(formData.customerInfo.email)) {
          newErrors.email = t.validation.invalidEmail;
        }
        break;

      case 'product':
        if (!formData.productDetails.quantity || formData.productDetails.quantity < 0.5) {
          newErrors.quantity = t.validation.minQuantity;
        }
        break;

      case 'payment':
        // No validation needed - selection and display only
        break;

      case 'terms':
        if (!formData.termsAccepted) {
          newErrors.termsAccepted = t.validation.termsRequired;
        }
        break;

      case 'sendToCustomer':
        // Validation handled in the component itself
        break;

      case 'signature':
        if (!formData.signature.name.trim()) {
          newErrors.signatureName = t.validation.required;
        }
        if (!formData.signature.location.trim()) {
          newErrors.signatureLocation = t.validation.required;
        }
        if (!formData.signature.signatureData && !formData.signature.typedSignature) {
          newErrors.signature = t.validation.signatureRequired;
        }
        if (!formData.signature.confirmationEmail.trim()) {
          newErrors.confirmationEmail = t.validation.required;
        } else if (!validateEmail(formData.signature.confirmationEmail)) {
          newErrors.confirmationEmail = t.validation.invalidEmail;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateCustomerNumber = async () => {
    try {
      setIsGeneratingCustomerNumber(true);
      const response = await fetch(`${API_URL}/api/customer-number/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_name: formData.customerInfo.name,
          customer_email: formData.customerInfo.email,
          company_name: formData.customerInfo.companyName || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate customer number');
      }

      const data = await response.json();
      
      // Update form data with the generated customer number
      setFormData(prev => ({
        ...prev,
        customerInfo: {
          ...prev.customerInfo,
          customerNumber: data.customer_number
        }
      }));

      return data.customer_number;
    } catch (error) {
      console.error('Error generating customer number:', error);
      toast.error(
        language === 'de'
          ? 'Fehler beim Generieren der Kundennummer'
          : 'Error generating customer number'
      );
      return null;
    } finally {
      setIsGeneratingCustomerNumber(false);
    }
  };

  const submitContractAndSendEmail = async (customerNumber) => {
    try {
      const response = await fetch(`${API_URL}/api/contract/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerInfo: {
            customerNumber: customerNumber,
            salutation: formData.customerInfo.salutation,
            name: formData.customerInfo.name,
            companyName: formData.customerInfo.companyName || null,
            commercialRegister: formData.customerInfo.commercialRegister || null,
            vatId: formData.customerInfo.vatId || null,
            address: formData.customerInfo.address,
            phone: formData.customerInfo.phone,
            email: formData.customerInfo.email,
          },
          productDetails: {
            quantity: formData.productDetails.quantity || 1,
            unitPrice: formData.productDetails.unitPrice || 58500,
            discount: formData.productDetails.discount || 0,
            discountAmount: formData.productDetails.discountAmount || 0,
            subtotal: formData.productDetails.subtotal || formData.productDetails.netPrice || 58500,
            vat: formData.productDetails.vat || formData.productDetails.vatAmount || 11115,
            totalPrice: formData.productDetails.totalPrice || 69615,
          },
          usePaymentPlan: formData.usePaymentPlan,
          signature: {
            name: formData.signature.name,
            companyName: formData.signature.companyName || null,
            location: formData.signature.location,
            date: formData.signature.date instanceof Date 
              ? formData.signature.date.toISOString().split('T')[0]
              : formData.signature.date,
            signatureDataUrl: formData.signature.signatureDataUrl,
            confirmationEmail: formData.signature.confirmationEmail || null,
          },
          language: language,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit contract');
      }

      const data = await response.json();
      
      // Store contract ID and timestamp for the success screen
      if (data.contract_id) {
        setContractId(data.contract_id);
        setSubmittedAt(new Date().toISOString());
      }
      
      if (data.email_sent) {
        toast.success(
          language === 'de'
            ? 'Vertrag wurde per E-Mail gesendet'
            : 'Contract sent via email'
        );
      }

      return true;
    } catch (error) {
      console.error('Error submitting contract:', error);
      // Don't block the flow if email fails, just log it
      toast.warning(
        language === 'de'
          ? 'Vertrag gespeichert, E-Mail-Versand fehlgeschlagen'
          : 'Contract saved, email sending failed'
      );
      return true; // Still return true to proceed
    }
  };

  const handleNext = async () => {
    if (validateStep(currentStepKey)) {
      // Generate customer number and submit contract when moving from signature to reservation
      if (currentStepKey === 'signature') {
        let customerNumber = formData.customerInfo.customerNumber;
        
        if (!customerNumber) {
          customerNumber = await generateCustomerNumber();
          if (!customerNumber) {
            return; // Don't proceed if customer number generation failed
          }
        }
        
        // Submit contract and send email
        await submitContractAndSendEmail(customerNumber);
      }

      if (!completedSteps.includes(currentStepKey)) {
        setCompletedSteps([...completedSteps, currentStepKey]);
      }
      setCurrentStep(prev => Math.min(prev + 1, activeSteps.length - 1));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    setErrors({});
  };

  const generatePDF = useCallback(() => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Header with yellow background
    doc.setFillColor(234, 179, 8); // Yellow
    doc.rect(0, 0, pageWidth, 35, 'F');

    // Title
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('RenewBee PowerPack', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(14);
    doc.text(t.title, pageWidth / 2, 25, { align: 'center' });

    yPos = 45;

    // Customer Number - prominently displayed
    if (formData.customerInfo.customerNumber) {
      doc.setFillColor(240, 253, 244); // Light green background
      doc.rect(15, yPos - 5, pageWidth - 30, 12, 'F');
      doc.setDrawColor(34, 197, 94); // Green border
      doc.rect(15, yPos - 5, pageWidth - 30, 12, 'S');
      
      doc.setTextColor(22, 163, 74); // Green text
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      const customerNumberLabel = language === 'de' ? 'Kundennummer:' : 'Customer Number:';
      doc.text(`${customerNumberLabel} ${formData.customerInfo.customerNumber}`, pageWidth / 2, yPos + 2, { align: 'center' });
      yPos += 18;
    } else {
      yPos += 5;
    }

    // Customer Information
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(t.customerInfo.title, 20, yPos);
    yPos += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const customerLines = [
      `${t.customerInfo.name}: ${formData.customerInfo.name}`,
      `${t.customerInfo.companyName}: ${formData.customerInfo.companyName || '-'}`,
      `${t.customerInfo.address}: ${formData.customerInfo.address}`,
      `${t.customerInfo.phone}: ${formData.customerInfo.phone}`,
      `${t.customerInfo.email}: ${formData.customerInfo.email}`
    ];

    customerLines.forEach(line => {
      doc.text(line, 20, yPos);
      yPos += 6;
    });

    yPos += 10;

    // Product Details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(t.productDetails.title, 20, yPos);
    yPos += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`${t.productDetails.productName}: ${t.productDetails.productNameValue}`, 20, yPos);
    yPos += 6;
    doc.text(`${t.productDetails.quantity}: ${formData.productDetails.quantity}`, 20, yPos);
    yPos += 6;
    
    // Gross price (before discount)
    const grossLabel = language === 'de' ? 'Bruttopreis' : 'Gross Price';
    doc.text(`${grossLabel}: ${formatCurrency(formData.productDetails.grossPrice || formData.productDetails.netPrice, locale)}`, 20, yPos);
    yPos += 6;
    
    // Discount (if applicable)
    if (formData.productDetails.discountPercent > 0) {
      doc.setTextColor(0, 128, 0); // Green for discount
      const discountLabel = language === 'de' ? 'Mengenrabatt' : 'Volume Discount';
      doc.text(`${discountLabel} (${formData.productDetails.discountPercent}%): -${formatCurrency(formData.productDetails.discountAmount, locale)}`, 20, yPos);
      yPos += 6;
      doc.setTextColor(60, 60, 60);
    }
    
    doc.text(`${t.productDetails.calculations.netPrice}: ${formatCurrency(formData.productDetails.netPrice, locale)}`, 20, yPos);
    yPos += 6;
    doc.text(`${t.productDetails.calculations.vat}: ${formatCurrency(formData.productDetails.vatAmount, locale)}`, 20, yPos);
    yPos += 6;

    doc.setFont('helvetica', 'bold');
    doc.text(`${t.productDetails.calculations.totalPrice}: ${formatCurrency(formData.productDetails.totalPrice, locale)}`, 20, yPos);
    yPos += 15;

    // Payment Plan or Full Payment
    doc.setFontSize(12);
    doc.text(t.paymentPlan.title, 20, yPos);
    yPos += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const totalPrice = formData.productDetails.totalPrice;

    if (formData.usePaymentPlan) {
      const payments = [
        { pct: '50%', amount: totalPrice * 0.5, desc: t.paymentPlan.payments[0].title },
        { pct: '25%', amount: totalPrice * 0.25, desc: t.paymentPlan.payments[1].title },
        { pct: '25%', amount: totalPrice * 0.25, desc: t.paymentPlan.payments[2].title }
      ];

      payments.forEach(p => {
        doc.text(`${p.desc} (${p.pct}): ${formatCurrency(p.amount, locale)}`, 20, yPos);
        yPos += 6;
      });
    } else {
      doc.text(language === 'de' 
        ? `Vollzahlung bei Vertragsunterzeichnung: ${formatCurrency(totalPrice, locale)}`
        : `Full payment at contract signing: ${formatCurrency(totalPrice, locale)}`, 20, yPos);
      yPos += 6;
    }

    yPos += 15;

    // Signature Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(t.signature.title, 20, yPos);
    yPos += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`${t.signature.name}: ${formData.signature.name}`, 20, yPos);
    yPos += 6;
    doc.text(`${t.signature.companyName}: ${formData.signature.companyName || '-'}`, 20, yPos);
    yPos += 6;
    doc.text(`${t.signature.location}: ${formData.signature.location}`, 20, yPos);
    yPos += 6;
    doc.text(`${t.signature.date}: ${formatDate(formData.signature.date, locale)}`, 20, yPos);
    yPos += 15;

    // Signature
    doc.text(`${t.signature.signatureLabel}:`, 20, yPos);
    yPos += 5;

    if (formData.signature.signatureType === 'draw' && formData.signature.signatureData) {
      try {
        doc.addImage(formData.signature.signatureData, 'PNG', 20, yPos, 60, 25);
      } catch (e) {
        doc.setFont('helvetica', 'italic');
        doc.text('[Digital Signature]', 20, yPos + 10);
      }
    } else if (formData.signature.typedSignature) {
      doc.setFontSize(16);
      doc.setFont('times', 'italic');
      doc.text(formData.signature.typedSignature, 20, yPos + 10);
    }

    yPos += 35;

    // Footer
    doc.setDrawColor(234, 179, 8);
    doc.setLineWidth(0.5);
    doc.line(20, yPos, pageWidth - 20, yPos);
    yPos += 10;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(t.footer.company, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
    doc.text(t.footer.address, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
    doc.text(`${t.footer.commercialRegister} | ${t.footer.vatId}`, pageWidth / 2, yPos, { align: 'center' });

    return doc;
  }, [formData, t, locale]);

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate PDF
      const pdf = generatePDF();
      
      // In a real application, you would send this to your backend
      // which would email the PDF to the customer and to customer@renewbee.co
      
      // For now, we'll just show success
      toast.success(
        language === 'de' 
          ? 'Vertrag erfolgreich eingereicht!' 
          : 'Contract successfully submitted!'
      );

      setIsSubmitted(true);
    } catch (error) {
      toast.error(
        language === 'de'
          ? 'Fehler beim Einreichen des Vertrags. Bitte versuchen Sie es erneut.'
          : 'Error submitting contract. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPdf = () => {
    const pdf = generatePDF();
    pdf.save(`RenewBee_Contract_${formData.customerInfo.name.replace(/\s+/g, '_')}.pdf`);
    toast.success(
      language === 'de' ? 'PDF heruntergeladen!' : 'PDF downloaded!'
    );
  };

  const handleSendToCustomer = async (customerEmail, personalMessage) => {
    // Now handled inside SendToCustomerSection directly
    setIsSentToCustomer(true);
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setCurrentStep(0);
    setErrors({});
    setCompletedSteps([]);
    setIsSubmitted(false);
    setIsSentToCustomer(false);
    setContractId(null);
    setSubmittedAt(null);
  };

  if (isSubmitted) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <SuccessScreen 
          formData={formData}
          onReset={handleReset}
          onDownloadPdf={handleDownloadPdf}
          t={t}
          language={language}
          contractId={contractId}
          submittedAt={submittedAt}
          apiUrl={API_URL}
        />
      </>
    );
  }

  const renderCurrentStep = () => {
    switch (currentStepKey) {
      case 'role':
        return (
          <RoleSelectionSection
            formData={formData}
            setFormData={setFormData}
            t={t}
          />
        );
      case 'customer':
        return (
          <CustomerInfoSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            t={t}
          />
        );
      case 'product':
        return (
          <ProductDetailsSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            t={t}
            language={language}
          />
        );
      case 'payment':
        return (
          <PaymentSection
            formData={formData}
            setFormData={setFormData}
            t={t}
            language={language}
          />
        );
      case 'terms':
        return (
          <TermsSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            t={t}
          />
        );
      case 'sendToCustomer':
        return (
          <SendToCustomerSection
            formData={formData}
            onSendToCustomer={handleSendToCustomer}
            isSubmitting={isSubmitting}
            t={t}
            language={language}
          />
        );
      case 'signature':
        return (
          <SignatureSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            t={t}
            language={language}
          />
        );
      case 'reservation':
        return (
          <PaymentReservationSection
            formData={formData}
            onPaymentSuccess={handlePaymentSuccess}
            t={t}
            language={language}
          />
        );
      default:
        return null;
    }
  };

  const handlePaymentSuccess = (sessionId) => {
    toast.success(
      language === 'de'
        ? 'Reservierungszahlung erfolgreich!'
        : 'Reservation payment successful!'
    );
    setIsSubmitted(true);
  };

  return (
    <>
      <Toaster position="top-center" richColors />
      
      {/* Progress Steps */}
      <ProgressSteps
        steps={t.steps}
        activeSteps={activeSteps}
        currentStep={currentStep}
        completedSteps={completedSteps}
        t={t}
      />

      {/* Form Content */}
      <div className="mt-6">
        {renderCurrentStep()}
      </div>

      {/* Navigation Buttons - Hide for sendToCustomer and reservation steps as they handle their own submission */}
      {currentStepKey !== 'sendToCustomer' && currentStepKey !== 'reservation' && (
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {t.buttons.previous}
          </Button>

          {currentStep < activeSteps.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={isGeneratingCustomerNumber}
              className="flex items-center gap-2"
              variant="premium"
            >
              {isGeneratingCustomerNumber ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {language === 'de' ? 'Generiere...' : 'Generating...'}
                </>
              ) : (
                <>
                  {t.buttons.next}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2"
              variant="success"
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t.buttons.submitting}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {t.buttons.submit}
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default ContractForm;
